import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { cn } from 'Utils/cn';
import { MentionsInput, Mention } from 'react-mentions';
import { trackError } from 'Utils/errorMonitoring';
import { getReferences } from 'Services/references';
import Spinner from 'Components/Atoms/Spinner';

export default function RHFCellInputReference({ name, className, onChange, onSelectReference, maxLength, ...other }) {
    const { control, clearErrors } = useFormContext();
    const [loading, setLoading] = useState(false);
    const [loadedReferences, setLoadedReferences] = useState([]);

    const loadReferences = (query, callback) => {
        if (!query) return;

        setLoading(true);
        setLoadedReferences([]);

        getReferences({ query, page: 1 })
            .then((response) => {
                const formattedReferences = response.items.map((reference) => {
                    return {
                        id: reference.id,
                        display: reference.name || '-',
                    };
                });
                setLoadedReferences(response.items);
                callback(formattedReferences);
            })
            .catch((error) => {
                trackError(error);
                setLoadedReferences([]);
                callback([]);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => {
                useEffect(() => {
                    if (field.value && error) {
                        clearErrors(name);
                    }
                }, [name, field.value, clearErrors]);

                const transformMentionValue = (value) => {
                    if (!value) return value;
                    // @[display](id) => display
                    return value.replace(/@\[([^\]]+)\]\([^)]+\)/g, '$1');
                };

                return (
                    <div className={cn('w-full', error && 'ring-1 ring-danger')}>
                        <MentionsInput
                            singleLine
                            allowSpaceInQuery
                            autoComplete="off"
                            className="mentions"
                            value={field.value || ''}
                            maxLength={maxLength}
                            onChange={(_event, newValue) => {
                                // react-mentions handles paste itself, so the native maxLength is not enforced
                                const transformedValue = transformMentionValue(newValue)?.slice(0, maxLength);
                                field.onChange(transformedValue);
                                if (onChange) {
                                    onChange(transformedValue);
                                }
                            }}
                            {...other}
                            allowSuggestionsAboveCursor
                            customSuggestionsContainer={(children) => (
                                <div className="p-1.5 rounded-md bg-white shadow-sm ring-1 ring-black/5 focus:outline-none z-dropdown">
                                    {loading ? (
                                        <div className="flex justify-center min-w-[100px]">
                                            <Spinner />
                                        </div>
                                    ) : (
                                        children
                                    )}
                                </div>
                            )}
                        >
                            <Mention
                                data={loadReferences}
                                onAdd={(id) => {
                                    const selectedReference = loadedReferences.find((reference) => reference.id === id);
                                    if (onSelectReference && selectedReference) {
                                        onSelectReference(selectedReference);
                                    }
                                }}
                                className={cn('mentions__mention', className)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault();
                                    }
                                }}
                                isLoading={loading}
                                renderSuggestion={(_suggestion, _search, highlightedDisplay, _index, focused) => (
                                    <div
                                        className={cn(
                                            'py-2 px-3 cursor-pointer text-sm rounded-sm transition-colors',
                                            focused ? 'bg-primary/10 text-primary' : 'bg-white text-dark-blue',
                                        )}
                                    >
                                        {highlightedDisplay}
                                    </div>
                                )}
                            />
                        </MentionsInput>
                    </div>
                );
            }}
        />
    );
}

RHFCellInputReference.propTypes = {
    name: PropTypes.string,
    className: PropTypes.string,
    onChange: PropTypes.func,
    onSelectReference: PropTypes.func,
    maxLength: PropTypes.number,
};
